import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { HelpTip } from "../components/HelpTip";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock3 } from "lucide-react";
import { timeAgo } from "../lib/format";

const STATE_CLASS = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  approved: "bg-emerald-50 text-emerald-900 border-emerald-300",
  blocked: "bg-red-50 text-red-900 border-red-300",
};

const STATE_HELP = {
  pending: "Waiting for you. Their invoices are visible but not delivered.",
  approved: "Their bills flow into your Receiving GL once items are mapped.",
  blocked: "Their invoices stay in LF and are never delivered.",
};

export default function Vendors() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, blocked: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = searchParams.get("state") || "all";
  const [filter, setFilter] = useState(initialFilter);

  useEffect(() => {
    const s = searchParams.get("state");
    if (s && s !== filter) setFilter(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = useCallback(async () => {
    const { data } = await api.get("/senders",
      { params: { state: filter === "all" ? "all" : filter } });
    setRows(data.items || []);
    setCounts(data.counts || {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const totalVendors = (counts.pending || 0) + (counts.approved || 0) + (counts.blocked || 0);

  const update = async (row, state) => {
    await api.put(`/senders/${encodeURIComponent(row.sender_org_key)}`, { state });
    await load();
  };

  const setChip = (s) => {
    setFilter(s);
    const next = new URLSearchParams(searchParams);
    if (s === "all") next.delete("state");
    else next.set("state", s);
    setSearchParams(next, { replace: true });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-8 py-10">
        <PageHeader
          eyebrow="company setup"
          title="Vendor list"
          description="The first time a new vendor sends you an invoice, approve them once. Nothing goes into your books from a vendor until you do."
          help="approving-vendors"
        />

        <div className="mt-8 flex items-center gap-2" data-testid="vendors-filters">
          {["all", "pending", "approved", "blocked"].map((s) => (
            <button
              key={s}
              type="button"
              className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${
                filter === s ? "border-black bg-black text-white" : "border-neutral-300 text-neutral-700"
              }`}
              onClick={() => setChip(s)}
              data-testid={`vendors-filter-${s}`}
            >
              {s} · {s === "all" ? (counts.pending || 0) + (counts.approved || 0) + (counts.blocked || 0) : counts[s] || 0}
            </button>
          ))}
        </div>

        <div className="mt-6 border border-neutral-200" data-testid="vendors-table">
          <div className="grid grid-cols-12 text-xs uppercase tracking-widest bg-neutral-50 border-b border-neutral-200 px-4 py-3">
            <div className="col-span-4">Vendor</div>
            <div className="col-span-3">Connector · GL org id</div>
            <div className="col-span-2">State</div>
            <div className="col-span-1">Seen</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {rows.length === 0 ? (
            totalVendors > 0 && filter !== "all" ? (
              <div className="px-4 py-16 text-center text-sm" data-testid="vendors-filtered-empty">
                <div className="text-neutral-700">No vendors match these filters.</div>
                <button type="button" className="btn-outline mt-4" onClick={() => setChip("all")}
                        data-testid="vendors-clear-filters">Clear filters</button>
              </div>
            ) : (
              <div className="px-4 py-16 text-center text-sm" data-testid="vendors-empty">
                <div className="text-neutral-700">
                  Vendors show up here the first time one sends you an invoice.
                </div>
                <Link to="/help#approving-vendors" className="btn-outline inline-block mt-4"
                      data-testid="vendors-empty-cta">How vendor approval works</Link>
              </div>
            )
          ) : rows.map((r) => (
            <div key={r.sender_org_key}
                 className="grid grid-cols-12 items-center px-4 py-3 border-b border-neutral-100 text-sm"
                 data-testid={`vendor-row-${r.sender_org_key}`}>
              <div className="col-span-4">
                <div className="font-medium" data-testid={`vendor-name-${r.sender_org_key}`}>
                  {(r.source_display_name && r.source_display_name !== "—" ? r.source_display_name : r.sender_display_name) || "—"}
                </div>
                <div className="text-xs text-neutral-500 flex items-center gap-2">
                  <span className="uppercase tracking-widest text-[10px] px-1.5 py-0.5 border border-neutral-200 text-neutral-600"
                        data-testid={`vendor-source-${r.sender_org_key}`}>
                    {r.source_erp}
                  </span>
                </div>
              </div>
              <div className="col-span-3 font-mono text-xs text-neutral-600">
                <div>{r.source_erp}</div>
                <div className="truncate">{r.sender_org_key}</div>
              </div>
              <div className="col-span-2">
                <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-widest border ${STATE_CLASS[r.state] || ""}`}
                      data-testid={`vendor-state-${r.sender_org_key}`}>
                  {r.state}
                </span>
                {STATE_HELP[r.state] && (
                  <HelpTip text={STATE_HELP[r.state]} anchor="approving-vendors"
                           label={`What ${r.state} means`}
                           testid={`vendor-state-help-${r.sender_org_key}`} />
                )}
                {r.decided_by_name && r.state !== "pending" && (
                  <span className="block text-[11px] text-neutral-500 mt-1" data-testid={`decided-by-${r.sender_org_key}`}>
                    {r.state === "approved" ? "Approved" : "Blocked"} by {r.decided_by_name} on {new Date(r.decided_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="col-span-1 text-xs text-neutral-500 flex items-center gap-1">
                <Clock3 size={10} /> {timeAgo(r.updated_at || r.created_at)}
              </div>
              <div className="col-span-2 text-right space-x-2">
                {r.state !== "approved" && (
                  <button className="btn-primary text-xs px-3 py-1 inline-flex items-center gap-1"
                          onClick={() => update(r, "approved")}
                          data-testid={`approve-${r.sender_org_key}`}>
                    <CheckCircle2 size={10} /> approve
                  </button>
                )}
                {r.state !== "blocked" && (
                  <button className="btn-outline text-xs px-3 py-1 inline-flex items-center gap-1"
                          onClick={() => update(r, "blocked")}
                          data-testid={`block-${r.sender_org_key}`}>
                    <XCircle size={10} /> block
                  </button>
                )}
                {r.state !== "pending" && (
                  <button className="btn-outline text-xs px-3 py-1"
                          onClick={() => {
                            const name = (r.source_display_name && r.source_display_name !== "—" ? r.source_display_name : r.sender_display_name) || r.sender_org_key;
                            if (window.confirm(`Move ${name} back to pending? New invoices from them will wait for your approval again. Bills already delivered stay in your accounting system.`)) update(r, "pending");
                          }}
                          data-testid={`pending-${r.sender_org_key}`}>
                    move back to pending
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
